import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Cake, EyeOff, Flower2, Handshake, MapPin, MoreHorizontal, Pencil, Share2, Star, UserRound } from "lucide-react";
import type { ContactDetail } from "@hub/shared";
import { Avatar, Button, IconButton, Menu, Modal, Pill, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useAskDrawer } from "@/app/askDrawer";
import { tierLabel, tierTone } from "@/features/people/tier";
import { SHARED_BY_FIELD, ShareContactSheet, cardInvolvesContact, useContactShares, useHubUserOf, useMatchCards } from "@/features/network";
import { useSession } from "@/app/session";
import type { ContactAction } from "./ContactActionHost";
import { ReachBar } from "./ReachLink";

const ORIGIN_LABEL: Record<string, string> = {
  manual: "Added by you",
  hub: "Added from Hub",
  apple_contacts: "Added from Apple Contacts",
  google: "Added from Google",
  microsoft: "Added from Microsoft 365",
  csv: "Imported from a CSV file",
  linkedin: "Imported from LinkedIn",
  calendar: "Added from your calendar",
  email: "Added from Mail",
  messages: "Added from Messages",
  calls: "Added from Phone",
  extension: "Added from the browser extension",
};

export function ContactHeader({ detail, onAction, compact }: { detail: ContactDetail; onAction: (a: ContactAction) => void; compact?: boolean }) {
  const navigate = useNavigate();
  const { profile, contact, statistics, isStarred } = detail;
  const [pickAvatar, setPickAvatar] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { isSignedIn } = useSession();
  const openAsk = useAskDrawer((d) => d.openAsk);
  const star = useEngineMutation("setContactStarred", { invalidate: ["getContact", "queryContacts", "getContactRows", "listGroups"] });
  const setAvatar = useEngineMutation("setContactAvatar", { invalidate: ["getContact", "queryContacts", "getContactRows"] });
  const setPrivate = useEngineMutation("setContactPrivate", { invalidate: ["getContact"] });
  const restore = useEngineMutation("restoreContact", { invalidate: ["getContact", "queryContacts", "searchArchivedContacts"] });
  const memorialize = useEngineMutation("setContactMemorialized", { invalidate: ["getContact", "queryContacts", "getContactRows", "getReconnectSuggestions", "getTimeline"] });
  const setSelf = useEngineMutation("setSelfContact", { invalidate: ["getSelfContact", "getContact", "queryContacts", "getContactRows"] });
  const cards = useMatchCards();
  const hubUserId = useHubUserOf(contact.id) ?? contact.linkedUserId;
  // Shares I started for this person (the pending state), and who shared them with me (the source line).
  const myShares = useContactShares(contact.id);
  const sharedByField = useEngineQuery("getContactFieldValues", { contactId: contact.id }, { enabled: Boolean(contact.sharedByUserId) });
  const sharedByName = sharedByField.data?.find((v) => v.fieldName === SHARED_BY_FIELD)?.value ?? null;
  const pendingShares = myShares.shares.filter((sh) => sh.status === "pending");
  const acceptedShares = myShares.shares.filter((sh) => sh.status === "accepted");
  const waiting = (cards.data?.cards ?? []).filter((c) => c.section === "waiting_on_you" && cardInvolvesContact(c, contact.id)).length;
  const bucket = statistics?.bucket ?? 4;
  const location = profile.addresses[0]?.city ?? null;
  const size = compact ? 56 : 80;
  const today = new Date();
  const birthdaySoon = profile.birthday && (() => { const d = new Date(today.getFullYear(), profile.birthday!.month - 1, profile.birthday!.day); const diff = (d.getTime() - today.getTime()) / 86_400_000; return diff >= -1 && diff <= 14; })();

  return (
    <header className="u-row" style={{ gap: compact ? 12 : 18, alignItems: "flex-start" }} data-testid="contact-header">
      <button type="button" onClick={() => setPickAvatar(true)} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", borderRadius: "50%", filter: contact.isMemorialized ? "grayscale(1)" : undefined, opacity: contact.isMemorialized ? 0.85 : undefined }} aria-label="Change photo" title="Change photo" data-memorialized={contact.isMemorialized ? "true" : undefined}>
        <Avatar name={profile.displayName} src={profile.avatarUrl} size={size} tier={bucket} seed={contact.id} />
      </button>
      <div className="u-grow" style={{ minWidth: 0 }}>
        <div className="u-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: compact ? 17 : 22, fontWeight: 650, lineHeight: 1.2 }}>{profile.displayName}</h1>
          {bucket < 4 && <Pill tone={tierTone(bucket)}>{tierLabel(bucket)}</Pill>}
          {hubUserId && <Pill tone="success">Hub user</Pill>}
          {contact.isPrivate && <Pill tone="warning" title="Excluded from matching and sharing"><EyeOff size={11} /> Private</Pill>}
          {contact.isArchived && <Pill tone="danger">Archived</Pill>}
          {contact.isMemorialized && <Pill tone="neutral" title="Kept for memory. No reconnect suggestions or birthday nudges."><Flower2 size={11} /> Memorialized</Pill>}
          {!contact.isMemorialized && birthdaySoon && <Pill tone="purple"><Cake size={11} /> Birthday soon</Pill>}
          {waiting > 0 && (
            <button type="button" onClick={() => navigate(`/contact/${contact.id}?tab=network`)} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", display: "inline-flex" }} title={`${waiting} introduction${waiting === 1 ? "" : "s"} waiting on you`} data-testid="waiting-match-pill">
              <Pill tone="accent"><Handshake size={11} /> {waiting === 1 ? "Intro waiting" : `${waiting} intros waiting`}</Pill>
            </button>
          )}
        </div>
        {profile.headline && <div style={{ marginTop: 2, color: "var(--text-secondary)" }}>{profile.headline}</div>}
        <div className="u-row u-small u-muted" style={{ marginTop: 4, gap: 12, flexWrap: "wrap" }}>
          {location && <span className="u-row" style={{ gap: 4 }}><MapPin size={12} />{location}</span>}
          {statistics?.lastInteractionDate && <span>Last talked {new Date(statistics.lastInteractionDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
          {contact.sharedByUserId ? <span data-testid="shared-by">Shared by {sharedByName ?? "a Hub user"}</span> : contact.originSource && <span>{ORIGIN_LABEL[contact.originSource] ?? `Added from ${contact.originSource.replace(/_/g, " ")}`}</span>}
          {pendingShares.length > 0 && (
            <span className="u-row" style={{ gap: 4 }} data-testid="share-pending" title="Waiting for them to accept or decline under Network, Shares">
              <Share2 size={12} />
              Shared with {pendingShares.map((sh) => sh.toUser.displayName).join(", ")}, pending
            </span>
          )}
          {pendingShares.length === 0 && acceptedShares.length > 0 && (
            <span className="u-row" style={{ gap: 4 }} data-testid="share-accepted">
              <Share2 size={12} />
              Shared with {acceptedShares.map((sh) => sh.toUser.displayName).join(", ")}
            </span>
          )}
        </div>
        <div style={{ marginTop: compact ? 10 : 12 }}>
          <ReachBar lookups={detail.lookups} />
        </div>
      </div>
      <div className="u-row" style={{ gap: 4, flex: "none" }}>
        <IconButton label={isStarred ? "Unstar (s)" : "Star (s)"} active={isStarred} onClick={() => star.mutate({ contactId: contact.id, starred: !isStarred })}>
          <Star size={16} fill={isStarred ? "currentColor" : "none"} />
        </IconButton>
        {!compact && (
          <>
            <Button variant="ghost" size="sm" icon={<Bot size={13} />} onClick={() => openAsk(contact.id)} title={`Ask Hub about ${profile.displayName}`}>
              Ask about {profile.firstName ?? profile.displayName.split(" ")[0]}
            </Button>
            <Button variant="outline" size="sm" icon={<Pencil size={13} />} onClick={() => navigate(`/contact/${contact.id}/edit`)}>
              Edit
            </Button>
          </>
        )}
        <Menu
          align="right"
          trigger={
            <IconButton label="More actions">
              <MoreHorizontal size={16} />
            </IconButton>
          }
          items={[
            { id: "edit", label: "Edit profile", onSelect: () => navigate(`/contact/${contact.id}/edit`) },
            { id: "merge", label: "Merge with another person", shortcut: "m", onSelect: () => onAction("merge") },
            { id: "cadence", label: "Reconnect cadence", shortcut: "h", onSelect: () => onAction("cadence") },
            { id: "d1", label: "", divider: true },
            contact.isPrivate
              ? { id: "unprivate", label: "Remove private mark", onSelect: () => setPrivate.mutate({ contactId: contact.id, isPrivate: false }, { onSuccess: () => toast("No longer private") }) }
              : { id: "private", label: "Mark as private", onSelect: () => onAction("private") },
            { id: "clear", label: "Clear profile data", onSelect: () => onAction("clear") },
            contact.isMemorialized
              ? { id: "unmemorialize", label: "Remove memorial", onSelect: () => memorialize.mutate({ contactId: contact.id, memorialized: false }, { onSuccess: () => toast("Memorial removed") }) }
              : { id: "memorialize", label: "Mark as memorialized", icon: <Flower2 size={14} />, onSelect: () => { if (window.confirm(`Mark ${profile.displayName} as memorialized? Their history stays; Hub stops suggesting reconnects and birthday reminders.`)) memorialize.mutate({ contactId: contact.id, memorialized: true }, { onSuccess: () => toast("Marked as memorialized") }); } },
            { id: "ask", label: `Ask Hub about ${profile.displayName}`, icon: <Bot size={14} />, onSelect: () => openAsk(contact.id) },
            { id: "share", label: "Share with a Hub user", icon: <Share2 size={14} />, disabled: !isSignedIn || contact.isPrivate, onSelect: () => setSharing(true) },
            { id: "me", label: "This is me", icon: <UserRound size={14} />, onSelect: () => { if (window.confirm(`Use ${profile.displayName} as your own card? Your needs and offers belong to this person.`)) setSelf.mutate({ contactId: contact.id }, { onSuccess: () => toast(`${profile.displayName} is now your own card.`, "success") }); } },
            { id: "d2", label: "", divider: true },
            contact.isArchived
              ? { id: "restore", label: "Restore from archive", onSelect: () => restore.mutate({ contactId: contact.id }, { onSuccess: () => toast("Restored") }) }
              : { id: "archive", label: "Archive", shortcut: "#", onSelect: () => onAction("archive") },
            { id: "delete", label: "Delete permanently", danger: true, onSelect: () => onAction("delete") },
          ]}
        />
      </div>
      <ShareContactSheet contactId={contact.id} contactName={profile.displayName} open={sharing} onClose={() => setSharing(false)} />
      <Modal open={pickAvatar} onClose={() => setPickAvatar(false)} title="Choose a photo">
        <div className="u-row" style={{ gap: 12, flexWrap: "wrap" }}>
          {detail.avatars.map((a) => (
            <button key={a.id} type="button" onClick={() => setAvatar.mutate({ contactId: contact.id, avatarId: a.id, url: a.url }, { onSuccess: () => setPickAvatar(false) })} style={{ border: profile.avatarUrl === a.url ? "2px solid var(--accent)" : "2px solid transparent", borderRadius: 12, padding: 6, background: "var(--bg-sunken)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <Avatar name={profile.displayName} src={a.url} size={64} />
              <span className="u-small u-muted">{a.source.replace(/_/g, " ")}</span>
            </button>
          ))}
          <button type="button" onClick={() => setAvatar.mutate({ contactId: contact.id, avatarId: null, url: null }, { onSuccess: () => setPickAvatar(false) })} style={{ border: "2px solid transparent", borderRadius: 12, padding: 6, background: "var(--bg-sunken)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <Avatar name={profile.displayName} size={64} />
            <span className="u-small u-muted">Initials</span>
          </button>
        </div>
      </Modal>
    </header>
  );
}
